import React, { useState, useMemo } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

/* ═══════════════════════════════════════════════════════════
   TPA / INSURANCE CLAIMS MODULE — Complete Revenue Cycle
   - Empanelled TPA / Insurance Companies
   - Pre-Authorization Requests
   - Claim Submission & Tracking
   - GIPSA / Cashless Processing
   - Rejection & Resubmission
   - MIS Reports
═══════════════════════════════════════════════════════════ */

const TPAS = ['Star Health Insurance','New India Assurance','United India Insurance','Oriental Insurance','National Insurance','HDFC ERGO','ICICI Lombard','Bajaj Allianz','Religare / Care Health','Niva Bupa (Max Bupa)','Medi Assist','Paramount Health','Vipul Medcorp','Raksha TPA','FHPL'];
const CLAIM_TYPES = ['Cashless - Planned','Cashless - Emergency','Reimbursement','Pre-Authorization','Enhancement Request','Post-Hospitalization'];
const DIAGNOSIS_CODES = ['I21 - Acute Myocardial Infarction','J18 - Pneumonia','N18 - Chronic Kidney Disease','K35 - Appendicitis','S72 - Fracture of femur','O34 - Cesarean Section','C50 - Breast Cancer','G35 - Multiple Sclerosis','E11 - Type 2 Diabetes','K80 - Cholelithiasis'];

const DEMO_CLAIMS = [];

function normalizeClaim(c) {
  return {
    ...c,
    id: c.claimId || c.id,
    patient: c.patientName || c.patient,
    uhid: c.patientId || c.uhid,
    claimedAmt: c.claimedAmt ?? c.claimAmt,
    diagnosis: c.diagnosis || c.diagnosisCode,
    submittedAt: c.submittedAt || c.date,
  };
}

const sColor = { Approved: '#10b981', Pending: '#f59e0b', Rejected: '#dc2626', 'Pre-Auth Approved': '#6366f1', Settled: '#059669', 'Under Query': '#f97316' };
const inr = n => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

function ClaimsListTab({ claims, onSelect, reload }) {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => claims.filter(c => {
    const matchFilter = filter === 'All' || c.status === filter;
    const matchSearch = !search || c.patient.toLowerCase().includes(search.toLowerCase()) || c.id.toLowerCase().includes(search.toLowerCase()) || c.policyNo.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  }), [claims, filter, search]);

  const totalClaimed = filtered.reduce((s, c) => s + (c.claimedAmt || 0), 0);
  const totalApproved = filtered.reduce((s, c) => s + (c.approvedAmt || 0), 0);
  const totalDeducted = filtered.reduce((s, c) => s + (c.deduction || 0), 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Claims', val: claims.length, color: '#6366f1' }, { label: 'Pending', val: claims.filter(c => c.status === 'Pending').length, color: '#f59e0b' }, { label: 'Approved', val: claims.filter(c => c.status === 'Approved' || c.status === 'Settled').length, color: '#10b981' }, { label: 'Rejected', val: claims.filter(c => c.status === 'Rejected').length, color: '#dc2626' }, { label: 'Total Claimed', val: inr(claims.reduce((s, c) => s + (c.claimedAmt || 0), 0)), color: '#3b82f6' }, { label: 'Total Approved', val: inr(claims.reduce((s, c) => s + (c.approvedAmt || 0), 0)), color: '#10b981' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 18, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder="Search patient, claim ID, policy no..." value={search} onChange={e => setSearch(e.target.value)} />
        {['All', 'Pending', 'Approved', 'Settled', 'Rejected', 'Pre-Auth Approved'].map(s => (
          <button key={s} type="button" className={`emr-chip ${filter === s ? 'active' : ''}`} style={{ fontSize: 11 }} onClick={() => setFilter(s)}>{s}</button>
        ))}
      </div>
      <div className="card">
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr><th>Claim ID</th><th>Patient</th><th>TPA / Insurer</th><th>Policy No.</th><th>Diagnosis</th><th>Type</th><th>Claimed</th><th>Approved</th><th>Deduction</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 700 }}>{c.id}</td>
                  <td><div style={{ fontWeight: 700 }}>{c.patient}</div><div style={{ fontSize: 10, color: '#94a3b8' }}>{c.uhid} · {c.age}y</div></td>
                  <td style={{ fontWeight: 600 }}>{c.tpa}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.policyNo}</td>
                  <td style={{ maxWidth: 160, fontSize: 11 }}>{c.diagnosis}</td>
                  <td style={{ fontSize: 11 }}>{c.claimType}</td>
                  <td style={{ fontWeight: 700, color: '#3b82f6' }}>{inr(c.claimedAmt)}</td>
                  <td style={{ fontWeight: 700, color: '#10b981' }}>{inr(c.approvedAmt)}</td>
                  <td style={{ fontWeight: 700, color: '#f97316' }}>{inr(c.deduction)}</td>
                  <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: (sColor[c.status] || '#94a3b8') + '18', color: sColor[c.status] || '#94a3b8', border: `1px solid ${(sColor[c.status] || '#94a3b8')}40`, whiteSpace: 'nowrap' }}>{c.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => onSelect(c)}>View</button>
                      {c.status === 'Rejected' && <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 10, padding: '2px 8px' }}
                        onClick={async () => {
                          try {
                            await ipApi.patchTpaClaim(c.id, { status: 'Pending' });
                            reload && reload();
                          } catch (err) {
                            alert(err.message || 'Failed to resubmit claim');
                          }
                        }}>Resubmit</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                <td colSpan={6} style={{ padding: '8px 10px' }}>Totals ({filtered.length} claims)</td>
                <td style={{ padding: '8px 10px', color: '#3b82f6', fontFamily: 'Outfit,sans-serif' }}>{inr(totalClaimed)}</td>
                <td style={{ padding: '8px 10px', color: '#10b981', fontFamily: 'Outfit,sans-serif' }}>{inr(totalApproved)}</td>
                <td style={{ padding: '8px 10px', color: '#f97316', fontFamily: 'Outfit,sans-serif' }}>{inr(totalDeducted)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function NewClaimTab({ onSave }) {
  const [form, setForm] = useState({ patient: '', uhid: '', age: '', gender: 'M', tpa: TPAS[0], policyNo: '', diagnosisCode: DIAGNOSIS_CODES[0], claimType: CLAIM_TYPES[0], admitDate: '', estimatedAmt: '', ward: '', attendingDoctor: '', contactPerson: '', contactPhone: '', notes: '' });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await ipApi.createTpaClaim({
        patient: form.patient,
        uhid: form.uhid,
        age: form.age,
        gender: form.gender,
        tpa: form.tpa,
        policyNo: form.policyNo,
        diagnosisCode: form.diagnosisCode,
        claimType: form.claimType,
        admitDate: form.admitDate,
        estimatedAmt: Number(form.estimatedAmt),
        ward: form.ward,
        attendingDoctor: form.attendingDoctor,
        contactPerson: form.contactPerson,
        contactPhone: form.contactPhone,
        notes: form.notes,
        status: 'Pending',
      });
      setSaved(true);
      onSave && onSave();
      setTimeout(() => setSaved(false), 3000);
      setForm({ patient: '', uhid: '', age: '', gender: 'M', tpa: TPAS[0], policyNo: '', diagnosisCode: DIAGNOSIS_CODES[0], claimType: CLAIM_TYPES[0], admitDate: '', estimatedAmt: '', ward: '', attendingDoctor: '', contactPerson: '', contactPhone: '', notes: '' });
    } catch (err) {
      alert(err.message || 'Failed to submit claim');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} style={{ borderLeft: '4px solid #6366f1' }}>
      <h3 style={{ marginTop: 0, color: '#6366f1' }}>📋 New Insurance / TPA Claim</h3>
      {saved && <div className="info-banner mb">✅ Claim submitted successfully.</div>}
      <h4 style={{ color: '#475569' }}>Patient Details</h4>
      <div className="form-grid mb">
        <div className="field"><label>Patient Name *</label><input className="input" required value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))} /></div>
        <div className="field"><label>UHID *</label><input className="input" required value={form.uhid} onChange={e => setForm(f => ({ ...f, uhid: e.target.value }))} /></div>
        <div className="field"><label>Age</label><input type="number" className="input" value={form.age} onChange={e => setForm(f => ({ ...f, age: e.target.value }))} /></div>
        <div className="field"><label>Gender</label><select className="input" value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option></select></div>
        <div className="field"><label>Attending Doctor</label><input className="input" value={form.attendingDoctor} onChange={e => setForm(f => ({ ...f, attendingDoctor: e.target.value }))} /></div>
        <div className="field"><label>Ward / Bed</label><input className="input" value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))} /></div>
        <div className="field"><label>Admission Date</label><input type="date" className="input" value={form.admitDate} onChange={e => setForm(f => ({ ...f, admitDate: e.target.value }))} /></div>
      </div>
      <h4 style={{ color: '#475569' }}>Insurance Details</h4>
      <div className="form-grid mb">
        <div className="field"><label>TPA / Insurer *</label><select className="input" required value={form.tpa} onChange={e => setForm(f => ({ ...f, tpa: e.target.value }))}>{TPAS.map(t => <option key={t}>{t}</option>)}</select></div>
        <div className="field"><label>Policy No. *</label><input className="input" required value={form.policyNo} onChange={e => setForm(f => ({ ...f, policyNo: e.target.value }))} /></div>
        <div className="field"><label>Claim Type *</label><select className="input" required value={form.claimType} onChange={e => setForm(f => ({ ...f, claimType: e.target.value }))}>{CLAIM_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
        <div className="field"><label>Diagnosis (ICD-10)</label><select className="input" value={form.diagnosisCode} onChange={e => setForm(f => ({ ...f, diagnosisCode: e.target.value }))}>{DIAGNOSIS_CODES.map(d => <option key={d}>{d}</option>)}</select></div>
        <div className="field"><label>Estimated Amount (₹) *</label><input type="number" className="input" required value={form.estimatedAmt} onChange={e => setForm(f => ({ ...f, estimatedAmt: e.target.value }))} /></div>
      </div>
      <h4 style={{ color: '#475569' }}>Contact for TPA</h4>
      <div className="form-grid mb">
        <div className="field"><label>Contact Person (Relative)</label><input className="input" value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} /></div>
        <div className="field"><label>Contact Phone</label><input className="input" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} /></div>
        <div className="field full"><label>Clinical Notes / History</label><textarea className="input" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary">Reset</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Submitting…' : '📤 Submit Claim to TPA'}</button>
      </div>
    </form>
  );
}

function TpaEmpanelmentTab() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getTpaEmpanelment(), []);
  const tpaList = (data?.tpaList || []).map((t) => ({
    ...t,
    tpa: t.tpaName || t.tpa,
    claimsThisMonth: t.claimsThisMonth || 0,
    amtThisMonth: t.amtThisMonth || 0,
  }));
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tpaName: TPAS[0], contactPerson: '', phone: '', email: '', tariffType: 'GIPSA', status: 'Active' });

  async function addTpa(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await ipApi.createTpaEmpanelment(form);
      await reload();
      setShowForm(false);
      setForm({ tpaName: TPAS[0], contactPerson: '', phone: '', email: '', tariffType: 'GIPSA', status: 'Active' });
    } catch (err) {
      alert(err.message || 'Failed to add TPA');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {error && <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></p>}
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancel' : '+ Add TPA Empanelment'}</button></div>
      {showForm && (
        <form className="card mb form-grid" onSubmit={addTpa}>
          <div className="field"><label>TPA / Insurer *</label><select className="input" required value={form.tpaName} onChange={(e) => setForm((f) => ({ ...f, tpaName: e.target.value }))}>{TPAS.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="field"><label>Contact Person</label><input className="input" value={form.contactPerson} onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))} /></div>
          <div className="field"><label>Phone</label><input className="input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
          <div className="field"><label>Email</label><input className="input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
          <div className="field"><label>Tariff Type</label><select className="input" value={form.tariffType} onChange={(e) => setForm((f) => ({ ...f, tariffType: e.target.value }))}>{['GIPSA', 'NHA', 'Custom'].map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="form-actions full"><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Empanelment'}</button></div>
        </form>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Empanelled TPAs & Insurance Companies</h3>
        {loading && tpaList.length === 0 ? <p className="hint">Loading empanelment…</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>TPA / Insurer</th><th>Contact</th><th>Tariff Type</th><th>Empanelled Since</th><th>Claims This Month</th><th>Revenue This Month</th><th>Status</th></tr></thead>
            <tbody>
              {tpaList.map(t => (
                <tr key={t.tpaId || t.tpa}>
                  <td style={{ fontWeight: 700 }}>{t.tpa}<div style={{ fontSize: 11, color: '#94a3b8' }}>{t.email}</div></td>
                  <td>{t.contactPerson}<div style={{ fontSize: 11, color: '#94a3b8' }}>{t.phone}</div></td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#eff6ff', color: '#2563eb' }}>{t.tariffType}</span></td>
                  <td>{t.empanelledSince}</td>
                  <td style={{ fontWeight: 700 }}>{t.claimsThisMonth}</td>
                  <td style={{ fontWeight: 700, color: '#10b981', fontFamily: 'Outfit,sans-serif' }}>{inr(t.amtThisMonth)}</td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: t.status === 'Active' ? '#f0fdf4' : '#f8fafc', color: t.status === 'Active' ? '#16a34a' : '#94a3b8', border: `1px solid ${t.status === 'Active' ? '#86efac' : '#e2e8f0'}` }}>{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        {tpaList.length === 0 && !loading && <p className="hint">No empanelled TPAs yet. Add one above.</p>}
      </div>
    </div>
  );
}

export default function TpaClaimsPage() {
  const [tab, setTab] = useState('claims');
  const [selected, setSelected] = useState(null);
  const { data, loading, error, reload } = useLiveData(() => ipApi.getTpaClaims(), []);
  const claims = (data?.claims || DEMO_CLAIMS).map(normalizeClaim);

  return (
    <PageShell title="TPA / Insurance Claims" subtitle="Pre-Authorization · Cashless Claims · Reimbursement · Empanelment · MIS"
      actions={<button type="button" className="btn btn-primary" onClick={() => setTab('new')}>+ New Claim</button>}>
      {error && <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></p>}
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'claims', label: '📋 All Claims' }, { id: 'new', label: '+ New Claim' }, { id: 'empanelment', label: '🤝 TPA Empanelment' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'claims' && (loading && claims.length === 0 ? <p className="hint">Loading claims…</p> : <ClaimsListTab claims={claims} onSelect={setSelected} reload={reload} />)}
      {tab === 'new' && <NewClaimTab onSave={() => { reload(); setTab('claims'); }} />}
      {tab === 'empanelment' && <TpaEmpanelmentTab />}
    </PageShell>
  );
}
